/**
 * The board of tasks: zooming it, moving around it, and arranging it.
 *
 * The board is already on the page when this runs. The server drew every
 * square where it stands and every line between them, so a browser with no
 * script shows the whole thing, follows a square to the window behind it and
 * comes back with the back button. What is added here is the three things
 * markup cannot do: making it larger and smaller, dragging the view around,
 * and - in the administration - dragging a square somewhere else.
 *
 * Zooming is one number. Every position, every size and the drawing itself
 * are written in board units multiplied by `--zoom`, so making the board
 * twice as large is that variable changing rather than a hundred elements
 * being told where they have moved to. It also means the scrollable area
 * grows with it, which a `transform` would not have done - a scaled board
 * would have kept the scrollbars of the unscaled one.
 *
 * Three things about the dragging are deliberate.
 *
 * A square is a link, and a link is something a browser drags by itself. Left
 * alone, the first pixel of a drag starts a native link drag, which cancels
 * the pointer stream - the square moves once and then stops dead. Every
 * square is therefore told it is not draggable, and the board refuses
 * `dragstart` outright.
 *
 * Where a square is going is worked out in board coordinates rather than from
 * how far the pointer has travelled. The board moves under the pointer while
 * it is being scrolled, so a drag measured in screen pixels drifts away from
 * the square the moment the view moves - and the view has to be able to move,
 * or nothing can be dragged further than one screen.
 *
 * Nothing is saved by zooming or panning. Where a square stands is the
 * team's decision and is saved; how far somebody has scrolled is not.
 */

import { postJson } from "../core/http";
import { query, queryAll } from "../core/dom";
import { text } from "../core/i18n";
import { toast } from "../core/toast";
import { announceHistoryChange } from "./ownHistory";

/** How far the board may be zoomed out, as a share of its own size. */
const SMALLEST = 0.3;

/** How far it may be zoomed in. */
const LARGEST = 2.5;

/** What one press of a button multiplies or divides the zoom by. */
const STEP = 1.25;

/**
 * How quickly a wheel changes the zoom.
 *
 * The change is a multiplication rather than an addition, so one notch feels
 * the same however far out the board already is. Added, the same step is a
 * third of the board near the bottom of the range and a twentieth near the
 * top.
 */
const WHEEL = 0.0016;

/** How much one notch of a wheel which reports whole lines counts for. */
const LINE = 16;

/**
 * How far a pointer may travel before it stops counting as a click.
 *
 * A square is a link as well as something to drag, and the empty board is
 * both something to pan and, in the administration, somewhere to put a new
 * task. Anything inside this is a hand which did not quite hold still.
 */
const SLOP = 4;

/** How close to the edge a dragged square starts scrolling the view. */
const EDGE = 48;

/** How fast the view scrolls while a square is held against its edge. */
const EDGE_SPEED = 14;

/** How far an arrow key moves a square, in board units. */
const NUDGE = 20;

/** How far it moves with a shift held. */
const LEAP = 100;

/** How wide and how tall one square is, in board units. */
const NODE = 100;

/** A square being dragged, and everything needed to keep it under the hand. */
interface Held {
    readonly node: HTMLElement;
    readonly pointerId: number;
    /** Where inside the square it was taken hold of, in board units. */
    readonly grabX: number;
    readonly grabY: number;
    /** Where the pointer was when it went down, on the screen. */
    readonly fromX: number;
    readonly fromY: number;
    /** Where the pointer is now, kept so the edge scroll can go on without it. */
    atX: number;
    atY: number;
    moved: boolean;
}

/** The view being dragged around, and where it was when that began. */
interface Panned {
    readonly pointerId: number;
    readonly fromX: number;
    readonly fromY: number;
    readonly left: number;
    readonly top: number;
    moved: boolean;
}

/**
 * Hold the pointer against *element* for the rest of the gesture.
 *
 * Asked for rather than assumed: a browser without pointer capture still
 * drags, it simply stops when the pointer leaves the element, which is worse
 * than the alternative of the whole board throwing.
 */
function holdPointer(element: HTMLElement, pointerId: number): void {
    element.setPointerCapture?.(pointerId);
}

/** Let the pointer go again, if it was ever held. */
function dropPointer(element: HTMLElement, pointerId: number): void {
    if (element.hasPointerCapture?.(pointerId)) {
        element.releasePointerCapture?.(pointerId);
    }
}

/** Return the zoom of *board*, whatever it currently stands at. */
function zoomOf(board: HTMLElement): number {
    const raw = Number.parseFloat(board.style.getPropertyValue("--zoom"));

    return Number.isFinite(raw) && raw > 0 ? raw : 1;
}

/** Return *value* brought inside the zoom the board allows. */
function clampZoom(value: number): number {
    return Math.min(LARGEST, Math.max(SMALLEST, Math.round(value * 100) / 100));
}

/** Return *value* brought inside the board, in board units. */
function clampToBoard(value: number, span: number): number {
    return Math.max(0, Math.min(Math.round(value), span));
}

/** Write the zoom onto the board and say what it now stands at. */
function applyZoom(board: HTMLElement, value: number): void {
    board.style.setProperty("--zoom", String(value));

    const label = query<HTMLElement>("[data-quest-zoom-label]", board);
    if (label) {
        label.textContent = `${Math.round(value * 100)} %`;
    }
}

/**
 * Change the zoom while keeping the point under the pointer where it is.
 *
 * Without this the board grows from its own top left corner, which throws
 * whatever somebody was looking at off the side of the screen on the very
 * first notch of the wheel.
 */
function zoomAround(board: HTMLElement, viewport: HTMLElement, next: number, atX: number, atY: number): void {
    const before = zoomOf(board);
    if (next === before) {
        return;
    }

    const box = viewport.getBoundingClientRect();
    const pointerX = atX - box.left + viewport.scrollLeft;
    const pointerY = atY - box.top + viewport.scrollTop;

    applyZoom(board, next);

    const ratio = next / before;
    viewport.scrollLeft += pointerX * ratio - pointerX;
    viewport.scrollTop += pointerY * ratio - pointerY;
}

/** Return how large the board is, in board units. */
function spanOf(board: HTMLElement): { width: number; height: number } {
    return {
        width: Number(board.dataset["boardWidth"] ?? 0),
        height: Number(board.dataset["boardHeight"] ?? 0),
    };
}

/** Return where *node* stands, in board units. */
function placeOf(node: HTMLElement): { x: number; y: number } {
    return { x: Number(node.dataset["questX"] ?? 0), y: Number(node.dataset["questY"] ?? 0) };
}

/** Put *node* at *x*, *y* on the board it stands on. */
function place(node: HTMLElement, x: number, y: number): void {
    node.dataset["questX"] = String(x);
    node.dataset["questY"] = String(y);
    node.style.setProperty("--quest-x", String(x));
    node.style.setProperty("--quest-y", String(y));
}

/** Redraw the lines which end at *node*, so they follow it as it moves. */
function followLines(board: HTMLElement, node: HTMLElement, x: number, y: number): void {
    const id = node.dataset["questId"];
    if (!id) {
        return;
    }

    const middleX = x + NODE / 2;
    const middleY = y + NODE / 2;

    for (const line of queryAll<SVGLineElement>("[data-quest-line]", board)) {
        if (line.dataset["from"] === id) {
            line.setAttribute("x1", String(middleX));
            line.setAttribute("y1", String(middleY));
        }

        if (line.dataset["to"] === id) {
            line.setAttribute("x2", String(middleX));
            line.setAttribute("y2", String(middleY));
        }
    }
}

/**
 * Return where on the board a point of the screen is, in board units.
 *
 * Read off the drawing itself rather than worked out from the scroll, so it
 * stays right while the view is moving underneath and while the zoom changes
 * halfway through a drag.
 */
function boardPoint(space: HTMLElement, zoom: number, atX: number, atY: number): { x: number; y: number } {
    const box = space.getBoundingClientRect();

    return { x: (atX - box.left) / zoom, y: (atY - box.top) / zoom };
}

/** Wire the buttons which zoom the board. */
function wireButtons(board: HTMLElement, viewport: HTMLElement, space: HTMLElement): void {
    for (const button of queryAll<HTMLElement>("[data-quest-zoom]", board)) {
        button.addEventListener("click", (event: MouseEvent) => {
            event.stopPropagation();

            const which = button.dataset["questZoom"];
            if (which === "reset") {
                showEverything(board, viewport, space);

                return;
            }

            const box = viewport.getBoundingClientRect();
            const next = which === "in" ? zoomOf(board) * STEP : zoomOf(board) / STEP;

            zoomAround(board, viewport, clampZoom(next), box.left + box.width / 2, box.top + box.height / 2);
        });
    }
}

/**
 * Zoom and scroll so that every square is on the screen at once.
 *
 * More useful than going back to a hundred per cent, which on a board whose
 * squares all stand a long way from the corner is a view of empty board. A
 * board which already fits is only scrolled, never blown up.
 */
function showEverything(board: HTMLElement, viewport: HTMLElement, space: HTMLElement): void {
    const nodes = queryAll<HTMLElement>("[data-quest-node]", space);
    if (!nodes.length) {
        applyZoom(board, 1);
        viewport.scrollTo({ left: 0, top: 0, behavior: "smooth" });

        return;
    }

    const places = nodes.map(placeOf);
    const left = Math.min(...places.map((at) => at.x));
    const top = Math.min(...places.map((at) => at.y));
    const right = Math.max(...places.map((at) => at.x)) + NODE;
    const bottom = Math.max(...places.map((at) => at.y)) + NODE;

    const room = viewport.getBoundingClientRect();
    const margin = 40;
    const fits = Math.min(
        (room.width - margin * 2) / Math.max(right - left, 1),
        (room.height - margin * 2) / Math.max(bottom - top, 1),
    );

    // Never larger than its own size: "show me everything" on a board of two
    // squares should not fill the screen with two enormous ones.
    const zoom = clampZoom(Math.min(fits, 1));
    applyZoom(board, zoom);

    viewport.scrollTo({
        left: Math.max(0, (left + (right - left) / 2) * zoom - room.width / 2),
        top: Math.max(0, (top + (bottom - top) / 2) * zoom - room.height / 2),
        behavior: "smooth",
    });
}

/** Wire the wheel, which zooms rather than scrolls while it is over a board. */
function wireWheel(board: HTMLElement, viewport: HTMLElement): void {
    viewport.addEventListener(
        "wheel",
        (event: WheelEvent) => {
            event.preventDefault();

            // A trackpad pinch arrives as a wheel with the control key held,
            // which is the same gesture said another way.
            const travelled = event.deltaMode === 1 ? event.deltaY * LINE : event.deltaY;
            const next = zoomOf(board) * Math.exp(-travelled * WHEEL);

            zoomAround(board, viewport, clampZoom(next), event.clientX, event.clientY);
        },
        { passive: false },
    );
}

/**
 * Wire dragging the view itself.
 *
 * Only from the empty parts of the board: starting a pan on a square would
 * mean a square could never be dragged, and in the administration that is the
 * more important of the two. The pointer is captured, so a pan which runs off
 * the edge of the board carries on instead of stopping where the element
 * does.
 */
function wirePanning(viewport: HTMLElement, wasPanned: { value: boolean }): void {
    let panning: Panned | null = null;

    viewport.addEventListener("pointerdown", (event: PointerEvent) => {
        const target = event.target;
        if (target instanceof Element && target.closest("[data-quest-node], [data-quest-controls]")) {
            return;
        }

        // The left button pans; the middle one is a paste on some systems and
        // the right one is the menu.
        if (event.button !== 0) {
            return;
        }

        // A finger already scrolls the board on its own, and doing it again
        // here would move it twice as far as the hand did.
        if (event.pointerType === "touch") {
            return;
        }

        panning = {
            pointerId: event.pointerId,
            fromX: event.clientX,
            fromY: event.clientY,
            left: viewport.scrollLeft,
            top: viewport.scrollTop,
            moved: false,
        };
        holdPointer(viewport, event.pointerId);
    });

    viewport.addEventListener("pointermove", (event: PointerEvent) => {
        if (panning === null || event.pointerId !== panning.pointerId) {
            return;
        }

        const movedX = event.clientX - panning.fromX;
        const movedY = event.clientY - panning.fromY;

        if (!panning.moved && Math.abs(movedX) < SLOP && Math.abs(movedY) < SLOP) {
            return;
        }

        panning.moved = true;
        viewport.classList.add("is-panning");
        viewport.scrollLeft = panning.left - movedX;
        viewport.scrollTop = panning.top - movedY;
    });

    const release = (event: PointerEvent): void => {
        if (panning === null || event.pointerId !== panning.pointerId) {
            return;
        }

        // Remembered rather than read off a class afterwards: the click which
        // follows has to know whether the hand was moving, and a class taken
        // off on a timer is a race with it.
        wasPanned.value = panning.moved;
        panning = null;

        viewport.classList.remove("is-panning");
        dropPointer(viewport, event.pointerId);
    };

    viewport.addEventListener("pointerup", release);
    viewport.addEventListener("pointercancel", release);
}

/**
 * Wire clicking the empty board, which is where a new task goes.
 *
 * The address carries the two numbers, so the window which opens is a page of
 * its own: a refused form comes back with the place still in it, and the whole
 * thing works with the script switched off as soon as somebody has an address
 * to type.
 */
function wireAdding(
    board: HTMLElement,
    viewport: HTMLElement,
    space: HTMLElement,
    addUrl: string,
    wasPanned: { value: boolean },
): void {
    viewport.addEventListener("click", (event: MouseEvent) => {
        const target = event.target;
        if (target instanceof Element && target.closest("[data-quest-node], [data-quest-controls]")) {
            return;
        }

        if (wasPanned.value) {
            wasPanned.value = false;

            return;
        }

        const span = spanOf(board);
        const at = boardPoint(space, zoomOf(board), event.clientX, event.clientY);

        // Dropped by its middle rather than its corner, because that is where
        // the pointer was and where somebody expects the square to appear.
        const atX = clampToBoard(at.x - NODE / 2, span.width - NODE);
        const atY = clampToBoard(at.y - NODE / 2, span.height - NODE);

        window.location.href = `${addUrl}&x=${atX}&y=${atY}`;
    });
}

/**
 * Wire dragging a square, which is the one thing here that is saved.
 *
 * Saved when it is let go of rather than while it moves: a request per frame
 * would be a hundred of them for one drag, and the answer of every one but the
 * last would be thrown away.
 */
function wireDragging(board: HTMLElement, viewport: HTMLElement, space: HTMLElement, moveUrl: string): void {
    let held: Held | null = null;
    let scrolling = 0;

    /** Put the held square wherever the pointer is now. */
    const follow = (): void => {
        if (held === null) {
            return;
        }

        const span = spanOf(board);
        const at = boardPoint(space, zoomOf(board), held.atX, held.atY);
        const atX = clampToBoard(at.x - held.grabX, span.width - NODE);
        const atY = clampToBoard(at.y - held.grabY, span.height - NODE);

        place(held.node, atX, atY);
        followLines(board, held.node, atX, atY);
    };

    /**
     * Scroll the view while a square is held against one of its edges.
     *
     * Without it a square can never be dragged further than one screen, which
     * on a board sixty squares across is most of it.
     */
    const creep = (): void => {
        if (held === null) {
            scrolling = 0;

            return;
        }

        const box = viewport.getBoundingClientRect();
        let byX = 0;
        let byY = 0;

        if (held.atX < box.left + EDGE) {
            byX = -EDGE_SPEED;
        } else if (held.atX > box.right - EDGE) {
            byX = EDGE_SPEED;
        }

        if (held.atY < box.top + EDGE) {
            byY = -EDGE_SPEED;
        } else if (held.atY > box.bottom - EDGE) {
            byY = EDGE_SPEED;
        }

        if (byX || byY) {
            viewport.scrollLeft += byX;
            viewport.scrollTop += byY;
            follow();
        }

        scrolling = window.requestAnimationFrame(creep);
    };

    space.addEventListener("pointerdown", (event: PointerEvent) => {
        const target = event.target;
        const node = target instanceof Element ? target.closest<HTMLElement>("[data-quest-draggable]") : null;

        if (!node || event.button !== 0) {
            return;
        }

        // The strip beside a square is pressed, never carried: starting a
        // drag on it would leave the button held down and never answered.
        if (target instanceof Element && target.closest("[data-quest-tools]")) {
            return;
        }

        const at = placeOf(node);
        const under = boardPoint(space, zoomOf(board), event.clientX, event.clientY);

        held = {
            node,
            pointerId: event.pointerId,
            grabX: under.x - at.x,
            grabY: under.y - at.y,
            fromX: event.clientX,
            fromY: event.clientY,
            atX: event.clientX,
            atY: event.clientY,
            moved: false,
        };

        holdPointer(node, event.pointerId);
        scrolling = window.requestAnimationFrame(creep);
    });

    space.addEventListener("pointermove", (event: PointerEvent) => {
        if (held === null || event.pointerId !== held.pointerId) {
            return;
        }

        held.atX = event.clientX;
        held.atY = event.clientY;

        if (!held.moved) {
            const far = Math.abs(event.clientX - held.fromX) >= SLOP || Math.abs(event.clientY - held.fromY) >= SLOP;
            if (!far) {
                return;
            }

            held.moved = true;
            held.node.classList.add("is-held");
        }

        event.preventDefault();
        follow();
    });

    const release = (event: PointerEvent): void => {
        if (held === null || event.pointerId !== held.pointerId) {
            return;
        }

        const { node, moved } = held;
        held = null;

        window.cancelAnimationFrame(scrolling);
        scrolling = 0;

        node.classList.remove("is-held");
        dropPointer(node, event.pointerId);

        if (!moved) {
            return;
        }

        // The square was dragged, so the click the browser is about to fire is
        // not one somebody meant. Swallowing it here is what stops a drag from
        // also opening the window of the task it moved.
        node.addEventListener("click", (click: Event) => click.preventDefault(), { once: true });

        void save(node, moveUrl);
    };

    space.addEventListener("pointerup", release);
    space.addEventListener("pointercancel", release);
}

/**
 * Wire the arrow keys, which move whichever square is focused.
 *
 * A board which can only be arranged by dragging is a board which cannot be
 * arranged without a mouse. Saved when the key comes back up, so holding one
 * down is one request rather than thirty.
 */
function wireKeys(board: HTMLElement, space: HTMLElement, moveUrl: string): void {
    const WAYS: Record<string, [number, number]> = {
        ArrowLeft: [-1, 0],
        ArrowRight: [1, 0],
        ArrowUp: [0, -1],
        ArrowDown: [0, 1],
    };

    let nudged: HTMLElement | null = null;

    space.addEventListener("keydown", (event: KeyboardEvent) => {
        const target = event.target;
        const node = target instanceof Element ? target.closest<HTMLElement>("[data-quest-draggable]") : null;
        const way = WAYS[event.key];

        if (!node || !way) {
            return;
        }

        event.preventDefault();

        const span = spanOf(board);
        const at = placeOf(node);
        const by = event.shiftKey ? LEAP : NUDGE;
        const atX = clampToBoard(at.x + way[0] * by, span.width - NODE);
        const atY = clampToBoard(at.y + way[1] * by, span.height - NODE);

        place(node, atX, atY);
        followLines(board, node, atX, atY);
        nudged = node;
    });

    space.addEventListener("keyup", (event: KeyboardEvent) => {
        if (nudged === null || !(event.key in WAYS)) {
            return;
        }

        const node = nudged;
        nudged = null;
        void save(node, moveUrl);
    });
}

/** Tell the portal where a square was let go of, and put it where it landed. */
async function save(node: HTMLElement, moveUrl: string): Promise<void> {
    const id = node.dataset["questId"];
    const at = placeOf(node);

    if (!id) {
        return;
    }

    try {
        const landed = await postJson<{ id: number; x: number; y: number }>(`${moveUrl}/${id}/move`, {
            x: at.x,
            y: at.y,
        });

        // The portal has the last word on where it stands - the edge of the
        // board is its rule, not the browser's.
        place(node, landed.x, landed.y);

        // Where the task stands is in the history of the tasks now, which
        // sits under this very board.
        announceHistoryChange();
    } catch {
        toast(text("js.quests.move_failed", "Polohu úlohy sa nepodarilo uložiť."), "error");
    }
}

/**
 * Stop the browser dragging the board's own links and pictures.
 *
 * A square is an `<a>`, and an `<a>` is something a browser drags by itself.
 * The first pixel of a drag would otherwise start a native link drag, which
 * cancels the pointer stream: the square moves once and then stops dead.
 */
function refuseNativeDrags(board: HTMLElement): void {
    // The slots and the links inside them both: the link is the one the
    // browser would drag, and the slot is what a stray selection would.
    for (const node of queryAll<HTMLElement>("[data-quest-node], .quest-node", board)) {
        node.setAttribute("draggable", "false");
    }

    board.addEventListener("dragstart", (event: Event) => event.preventDefault());
}

/** Wire every board of the current page. */
export function initQuestBoards(): void {
    for (const board of queryAll<HTMLElement>("[data-quest-board]")) {
        const viewport = query<HTMLElement>("[data-quest-viewport]", board);
        const space = query<HTMLElement>("[data-quest-space]", board);

        if (!viewport || !space) {
            continue;
        }

        const wasPanned = { value: false };

        applyZoom(board, zoomOf(board));
        refuseNativeDrags(board);
        wireButtons(board, viewport, space);
        wireWheel(board, viewport);
        wirePanning(viewport, wasPanned);

        const moveUrl = board.dataset["questMove"];
        if (board.hasAttribute("data-quest-editable") && moveUrl) {
            wireDragging(board, viewport, space, moveUrl);
            wireKeys(board, space, moveUrl);
        }

        const addUrl = board.dataset["questAdd"];
        if (addUrl) {
            wireAdding(board, viewport, space, addUrl, wasPanned);
        }
    }

    upgradeWindows();
}

/**
 * Turn the windows the server left open into real modal ones.
 *
 * A `<dialog open>` is a panel: it is on the page, but the page behind it is
 * still reachable and Escape does nothing. `showModal` is what gives it the
 * backdrop, the trapped focus and the key everybody presses to shut it. It is
 * done here rather than in the markup because a dialog opened that way cannot
 * be opened at all without a script, and the panel is the fallback.
 */
function upgradeWindows(): void {
    if (typeof HTMLDialogElement !== "function" || typeof HTMLDialogElement.prototype.showModal !== "function") {
        return;
    }

    for (const dialog of queryAll<HTMLDialogElement>("[data-quest-window], [data-quest-form]")) {
        const back = query<HTMLAnchorElement>("[data-quest-close]", dialog);

        dialog.open = false;
        dialog.showModal();

        // Escape and the backdrop both close a modal dialog, and closing one
        // of these means leaving the address it is open at.
        dialog.addEventListener("close", () => {
            if (back) {
                window.location.href = back.href;
            }
        });
    }
}

/** Kept apart so a test can drive one board without touching the page. */
export { applyZoom, boardPoint, clampToBoard, clampZoom, followLines, place, placeOf, showEverything, zoomOf };
